import React, { useState } from 'react';
import CloudflareVideo, { ResponsiveCloudflareVideo } from './CloudflareVideo';
import CloudflareImage from './CloudflareImage';

/**
 * MediaGallery Component
 * A comprehensive gallery that can display both Cloudflare Images and Stream videos
 */
const MediaGallery = ({
  items = [],
  columns = 3,
  gap = '1rem',
  className = '',
  showTitles = true,
  lightbox = true,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = (item, index) => {
    if (lightbox) {
      setSelectedItem({ ...item, index });
      setLightboxOpen(true);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedItem(null);
  };

  const navigateLightbox = (direction) => {
    if (!selectedItem) return;
    
    const currentIndex = selectedItem.index;
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % items.length
      : (currentIndex - 1 + items.length) % items.length;
    
    setSelectedItem({ ...items[newIndex], index: newIndex });
  };

  const galleryStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(300px, 1fr))`,
    gap,
    width: '100%',
  };

  return (
    <>
      <div className={`media-gallery ${className}`} style={galleryStyle}>
        {items.map((item, index) => (
          <div
            key={item.id}
            className="media-item"
            style={{
              cursor: lightbox ? 'pointer' : 'default',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onClick={() => openLightbox(item, index)}
            onMouseEnter={(e) => {
              if (lightbox) {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (lightbox) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }
            }}
          >
            {item.type === 'video' ? (
              <div>
                <CloudflareVideo
                  videoId={item.id}
                  width="100%"
                  height="200"
                  autoplay={false}
                  controls={!lightbox} // Don't show controls in gallery view if lightbox enabled
                  showThumbnail={true}
                />
                {showTitles && item.title && (
                  <div style={{ padding: '1rem', backgroundColor: '#f9f9f9' }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                      {item.title}
                    </h3>
                    {item.description && (
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        {item.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <CloudflareImage
                  imageId={item.id}
                  variant="public"
                  alt={item.title || 'Gallery image'}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                  }}
                />
                {showTitles && item.title && (
                  <div style={{ padding: '1rem', backgroundColor: '#f9f9f9' }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                      {item.title}
                    </h3>
                    {item.description && (
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        {item.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && selectedItem && (
        <div
          className="lightbox-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeLightbox}
        >
          <div
            className="lightbox-content"
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                zIndex: 1001,
                fontSize: '18px',
              }}
            >
              ×
            </button>

            {/* Navigation buttons */}
            {items.length > 1 && (
              <>
                <button
                  onClick={() => navigateLightbox('prev')}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    zIndex: 1001,
                    fontSize: '18px',
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={() => navigateLightbox('next')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    zIndex: 1001,
                    fontSize: '18px',
                  }}
                >
                  ›
                </button>
              </>
            )}

            {/* Content */}
            {selectedItem.type === 'video' ? (
              <ResponsiveCloudflareVideo
                videoId={selectedItem.id}
                aspectRatio="16:9"
                maxWidth="800px"
                autoplay={false}
                controls={true}
              />
            ) : (
              <CloudflareImage
                imageId={selectedItem.id}
                variant="public"
                alt={selectedItem.title || 'Lightbox image'}
                style={{
                  maxWidth: '800px',
                  maxHeight: '600px',
                  width: 'auto',
                  height: 'auto',
                }}
              />
            )}

            {/* Title and description */}
            {(selectedItem.title || selectedItem.description) && (
              <div style={{ padding: '1rem' }}>
                {selectedItem.title && (
                  <h2 style={{ margin: '0 0 0.5rem 0' }}>
                    {selectedItem.title}
                  </h2>
                )}
                {selectedItem.description && (
                  <p style={{ margin: 0, color: '#666' }}>
                    {selectedItem.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Example data structure for the gallery
export const sampleMediaItems = [
  {
    id: 'your-video-id-1',
    type: 'video',
    title: 'Sample Video 1',
    description: 'This is a sample video from Cloudflare Stream',
  },
  {
    id: 'your-video-id-2',
    type: 'video',
    title: 'Sample Video 2',
    description: 'Another sample video',
  },
  {
    id: 'your-image-id-1',
    type: 'image',
    title: 'Sample Image 1',
    description: 'This is a sample image from Cloudflare Images',
  },
  {
    id: 'your-image-id-2',
    type: 'image',
    title: 'Sample Image 2',
    description: 'Another sample image',
  },
];

export default MediaGallery;